import { Fragment } from 'react';
import { CF_LEVEL_STYLE, CF_TEXT_STYLE, cfCompute, type CfComing, type CfComingRow, type CfTask } from './careFlow';
import { A, sx } from './parts';

// The V7 escalation ladder, drawn as the design draws it (Aithora ConsoleV7.dc.html: the row's
// ladder T1561 / T1720, the "Moving up the ladder" box T1458 / T1627).

/** The ladder under one task or request: level, the chain of people, where it is now. */
export function LadderLine({
  task,
  now,
  hideLevel,
  canClaim,
  onClaim,
  onUndo,
  disabled,
  title,
}: {
  task: CfTask;
  now: number;
  hideLevel?: boolean;
  /** "I'm on it" shows (an open task the viewer can act on). */
  canClaim: boolean;
  onClaim?: () => void;
  /** Undo shows on a claimed task the viewer can act on. */
  onUndo?: (() => void) | null;
  disabled?: boolean;
  title?: string;
}) {
  const r = cfCompute(task, now);
  const showClaim = canClaim && !task.done && !r.claimed;
  const showUndo = !!onUndo && r.claimed && !task.done;
  return (
    <span data-cf-row={task.id} title={`Log: ${r.audit}`} style={sx('display:flex;flex-direction:column;gap:5px;margin-top:5px;min-width:0;')}>
      <span style={sx('display:flex;align-items:center;gap:4px;flex-wrap:wrap;row-gap:4px;')}>
        {!hideLevel && <span style={sx(CF_LEVEL_STYLE(r.level))}>{r.level.label}</span>}
        {r.chips.map((c, i) => (
          <Fragment key={i}>
            <span data-cf-chip={`${task.id}|${i}`} style={sx(c.s)}>
              {c.t}
            </span>
            {i < r.chips.length - 1 && (
              <span aria-hidden="true" style={sx('font-size:14px;color:#54606C;')}>
                &#8594;
              </span>
            )}
          </Fragment>
        ))}
      </span>
      <span style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;')}>
        <span data-cf-text="1" style={sx(CF_TEXT_STYLE(r.ink))}>
          {r.text}
        </span>
        {showClaim && (
          <A
            style="display:inline-flex;align-items:center;justify-content:center;height:30px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:800;text-decoration:none;white-space:nowrap;box-sizing:border-box;color:#FFFFFF;background:#264C7E;border:0;"
            ink
            onClick={() => onClaim?.()}
            disabled={disabled}
            title={title}
          >
            I&rsquo;m on it
          </A>
        )}
        {showUndo && (
          <A
            style="display:inline-flex;align-items:center;justify-content:center;height:30px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:800;text-decoration:none;white-space:nowrap;box-sizing:border-box;color:#264C7E;background:transparent;border:1px solid rgba(47,92,151,.4);"
            onClick={() => onUndo?.()}
            disabled={disabled}
            title={title}
          >
            Undo
          </A>
        )}
      </span>
    </span>
  );
}

/** "Moving up the ladder": what is about to move, or already moved, to the next person. */
export function MovingUpBox({
  c,
  canAssign,
  onMore,
  onTake,
  onGive,
  disabled,
  title,
}: {
  c: CfComing;
  canAssign: boolean;
  onMore: () => void;
  onTake: (row: CfComingRow) => void;
  onGive: (row: CfComingRow, userId: string, name: string) => void;
  disabled?: boolean;
  title?: string;
}) {
  if (!c.show) return null;
  return (
    <div data-screen-label="Moving up the ladder" style={sx('flex:0 0 auto;display:flex;flex-direction:column;min-width:0;margin-bottom:8px;padding:8px 12px;border-radius:10px;border:1px solid rgba(34,39,46,.12);')}>
      <div style={sx('display:flex;align-items:center;gap:4px 10px;flex-wrap:wrap;min-width:0;padding-bottom:6px;')}>
        <span style={sx('font-size:14px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#7A4A0E;')}>Moving up the ladder</span>
        <span style={sx('font-size:14px;font-weight:700;color:#7A4A0E;font-variant-numeric:tabular-nums;')}>{String(c.n)}</span>
        <span style={sx('flex:1 1 220px;min-width:0;font-size:14px;color:#3C454F;')}>{c.sum}</span>
        {c.moreShow && (
          <A style="font-size:14px;font-weight:700;color:#264C7E;text-decoration:none;white-space:nowrap;" onClick={onMore}>
            {c.moreLabel}
          </A>
        )}
      </div>
      {c.rows.map((m) => (
        <div key={m.id} data-cf-row={m.id} style={sx('display:flex;align-items:center;gap:4px 12px;flex-wrap:wrap;min-width:0;padding:6px 0;border-top:1px solid rgba(34,39,46,.08);')}>
          <span style={sx(m.dot)} />
          <span style={sx('flex:1 1 300px;min-width:0;display:flex;align-items:baseline;gap:2px 10px;flex-wrap:wrap;')}>
            <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{m.label}</span>
            <span data-cf-text="1" style={sx(m.ink)}>
              {m.text}
            </span>
          </span>
          {canAssign && (
            <span style={sx('display:flex;gap:6px;flex-wrap:wrap;align-items:center;')}>
              <A
                style="display:inline-flex;align-items:center;justify-content:center;min-height:30px;padding:0 10px;border-radius:7px;font-size:14px;font-weight:800;text-decoration:none;white-space:nowrap;color:#FFFFFF;background:#264C7E;"
                ink
                onClick={() => onTake(m)}
                disabled={disabled}
                title={title}
              >
                I&rsquo;ll take it
              </A>
              {m.picks.map((p) => (
                <A
                  key={p.userId}
                  style="display:inline-flex;align-items:center;justify-content:center;min-height:30px;padding:0 10px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;color:#264C7E;background:transparent;border:1px solid rgba(47,92,151,.4);"
                  onClick={() => onGive(m, p.userId, p.name)}
                  disabled={disabled}
                  title={title}
                >
                  {`Give to ${p.name}`}
                </A>
              ))}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
